import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../context/AuthContext";

/**
 * Customer-facing "Report business" modal. Opens from a business profile page
 * or the customer's booking history. Posts to POST /api/reports, which triages
 * via Claude Sonnet 4.5 and creates a Case for human review.
 */
const CATEGORIES = [
  { value: "", label: "Choose a category..." },
  { value: "safety", label: "Safety concern" },
  { value: "fraud", label: "Fraud / scam" },
  { value: "no_show", label: "Provider didn't show up" },
  { value: "quality", label: "Poor service quality" },
  { value: "harassment", label: "Harassment / inappropriate behavior" },
  { value: "forged_documents", label: "Forged / fake documents" },
  { value: "violence", label: "Violence or physical threat" },
  { value: "billing", label: "Billing / payment dispute" },
  { value: "misrepresentation", label: "Business misrepresented services" },
  { value: "other", label: "Other" },
];

export default function ReportBusinessModal({ businessId, businessName, bookingId, onClose, onSubmitted }) {
  const { authHeader, user } = useAuth();
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const canReport = !!user && user.role === "customer";

  const submit = async () => {
    if (!canReport) { toast.error("Sign in as a customer to file a report"); return; }
    if (!category) { toast.error("Please pick a category"); return; }
    if (description.trim().length < 10) { toast.error("Please describe what happened (at least 10 characters)"); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/reports`,
        { business_id: businessId, category, description: description.trim(), booking_id: bookingId || undefined },
        { headers: authHeader() },
      );
      toast.success(r.data?.message || "Report received. Our team will follow up.");
      onSubmitted && onSubmitted(r.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
         onClick={onClose} data-testid="report-business-modal">
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
           style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#dc2626" }}>Report</div>
        <h3 className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>
          Report {businessName || "this business"}
        </h3>
        <p className="text-sm mt-2" style={{ color: "var(--gf-text-soft)" }}>
          Our team reviews every report. Serious concerns (safety, fraud, violence) are prioritized. False or malicious
          reports may result in restrictions on your own account.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
              Category
            </label>
            <select
              data-testid="report-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value} style={{ color: "#000" }}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
              What happened?
            </label>
            <textarea
              data-testid="report-description-input"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please give as much detail as you can — when, where, who, and what specifically happened."
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
              {description.length} / 4000 characters
            </p>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose}
                  className="text-sm rounded-full border px-4 py-2"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)" }}>
            Cancel
          </button>
          <button
            data-testid="report-submit-btn"
            onClick={submit}
            disabled={busy || !category || description.trim().length < 10}
            className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
            style={{ background: "#dc2626", color: "#fff" }}
          >
            {busy ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );
}
